import { useStytch } from "@stytch/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAtom } from "jotai";
import { useEffect, useRef, type JSX } from "react";

import { returnToAtom } from "@/lib/atoms/return-to-atom";
import {
  captureAuthError,
  trackLoginFailed,
  trackLoginSucceeded,
} from "@/lib/auth/auth-tracking";
import { isInternalPath } from "@/lib/utils/is-internal-path";

const AuthCallback = (): JSX.Element => {
  const stytch = useStytch();
  const navigate = useNavigate();
  const [returnTo, setReturnTo] = useAtom(returnToAtom);
  const destinationRef = useRef(
    returnTo && isInternalPath(returnTo) ? returnTo : "/auth/oauth/authorize",
  );

  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(globalThis.location.search);
      const token = params.get("token");
      const tokenType = params.get("stytch_token_type");

      if (tokenType === "otp") {
        setReturnTo(null);
        await navigate({ to: destinationRef.current, replace: true });
        return;
      }

      if (!token || !tokenType) {
        throw new Error("Missing or invalid OAuth token");
      }

      if (tokenType === "oauth") {
        await stytch.oauth.authenticate(token, { session_duration_minutes: 10080 });
        trackLoginSucceeded("social");
      } else {
        throw new Error(`Unsupported token type: ${tokenType}`);
      }

      setReturnTo(null);
      await navigate({ to: destinationRef.current, replace: true });
    };

    run().catch((error: unknown) => {
      trackLoginFailed("social", error);
      captureAuthError(error, { flow: "login", method: "social" });
      void navigate({ to: "/auth/signin", replace: true });
    });
  }, [stytch, navigate, setReturnTo]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4">
      <h2 className="text-xl font-semibold text-gray-800">Autenticando...</h2>
      <p className="text-sm text-gray-500">
        Aguarde enquanto verificamos suas credenciais.
      </p>
    </div>
  );
};

export const Route = createFileRoute("/auth/idp-callback")({
  component: AuthCallback,
});
